import React, { useMemo } from 'react';
import classNames from 'classnames';
import { DataRoomArchive } from '../types';
import ProgressBar from 'components/ProgressBar';
import { useTranslation } from 'react-i18next';
import { getPercentageWithTwoDecimals } from 'utils';
import { checkIsArchiveCompleted, STEPS_LENGTH } from '../utils';
import ArchiveInstance from './components/ArchiveInstance';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import useDownloadArchiveInstance from 'router/subrouters/DataRoom/hooks/useDownloadArchiveInstance';

import './Archive.styles.scss';

type ArchiveProps = {
  className?: string;
  archive: DataRoomArchive;
  updateArchive: (archive: DataRoomArchive) => void;
};

const Archive: React.FC<ArchiveProps> = (props) => {
  const { className, archive, updateArchive } = props;

  const {
    _id: archiveId,
    downloadIndex,
    lastStep,
    instances,
    stepsCompletedDates,
  } = archive;

  const { t } = useTranslation();

  const classes = classNames('archive', className);

  const { handleDownload } = useDownloadArchiveInstance();

  const isLastStepComplete = checkIsArchiveCompleted(lastStep);

  const percentage = useMemo(
    () =>
      getPercentageWithTwoDecimals(stepsCompletedDates.length, STEPS_LENGTH),
    [stepsCompletedDates],
  );

  useEffectSkipFirst(() => {
    if (!isLastStepComplete || !downloadIndex) return;

    instances.forEach(({ _id }) => handleDownload(archiveId, _id));
  }, [isLastStepComplete]);

  if (!isLastStepComplete) {
    return (
      <div className={classNames(classes, 'archive--processing')}>
        <ProgressBar percentage={percentage} />
        <p>{t('generatingArchive') + '...'}</p>
      </div>
    );
  }

  return (
    <div className={classes}>
      {instances.map((instance) => (
        <ArchiveInstance
          key={instance._id}
          archiveInstance={instance}
          updateArchive={updateArchive}
        />
      ))}
    </div>
  );
};

export default Archive;
